/**
 * useNotificationsRealtime — SSE client para o feed unificado de notificacoes.
 *
 * Spec docs/notificacoes/IMPLEMENTATION_SPEC_V1V2.md §3.2 + §3.7 (TASK-2577).
 *
 * Conecta no endpoint backend `GET /api/v1/me/notifications/feed/stream`
 * (TASK-2569) via EventSource e conta eventos novos recebidos desde o
 * ultimo refetch. A pagina /notificacoes usa o counter para renderizar o
 * NewItemsBanner.
 *
 * Comportamento:
 *  - Conecta no mount e MANTÉM a conexão viva mesmo com a aba em segundo plano
 *    (entrega em tempo real "estilo WhatsApp" — a mensagem chega na hora mesmo
 *    com a aba atrás). O limite de conexões por origem é mitigado pelo
 *    SharedWorker (1 SSE compartilhado entre todas as abas da origem).
 *  - Reconecta ao voltar o foco caso a conexão tenha caído enquanto oculta.
 *  - Auto-reconect default do EventSource em erros transientes
 *  - Filtra eventos por sources/severity quando o usuario tem filtros
 *    ativos (so conta o que importa para o filtro corrente)
 *  - Reseta o counter quando os filtros mudam (refetch implicito)
 *  - Cleanup completo do EventSource no unmount
 *
 * Transport:
 *  - Primario: SharedWorker (notifications-sse-worker) compartilha 1
 *    conexao SSE entre todas as tabs, contornando o limite ~6 conn/origin
 *    do browser quando o usuario abre varias abas + AuditBell.
 *  - Fallback: EventSource direto quando SharedWorker indisponivel
 *    (mobile Safari, alguns embeds).
 *
 * Sem dependencias novas — apenas React + EventSource nativa + SharedWorker.
 */
import { useEffect, useRef, useState } from 'react'

const SSE_PATH = '/api/v1/me/notifications/feed/stream'

export interface RealtimeEvent {
  event_id?: number
  source?: string
  severity?: string
  title?: string
  event_type?: string
  /** Tipo do evento de chat que trafega no mesmo feed SSE (migração WS→SSE):
   *  'message.created'|'message.updated'|'message.deleted'|'reaction.updated'|'read'.
   *  Eventos de notificação não têm este campo. */
  type?: string
}

export interface UseRealtimeOptions {
  /** Chamado para CADA evento recebido (independente dos filtros). Usado pelo
   *  toast in-app global, que faz seu próprio matching por preferências. */
  onEvent?: (ev: RealtimeEvent) => void
}

export interface UseRealtimeReturn {
  newItemsCount: number
  resetCount: () => void
  connected: boolean
}

interface SseConnection {
  close: () => void
}

const useSharedWorker =
  typeof SharedWorker !== 'undefined' && typeof window !== 'undefined'

function openSseConnection(
  onEvent: (data: string) => void,
  onConnected: () => void,
  onError: () => void,
): SseConnection {
  // O EventSource não envia header Authorization → mandamos o token de sessão
  // via query param (mesma fonte do interceptor axios). Sem isso: 401 → sem realtime.
  const token = (typeof localStorage !== 'undefined' && localStorage.getItem('session_token')) || ''
  if (useSharedWorker) {
    try {
      const worker = new SharedWorker(
        new URL('../workers/notifications-sse-worker.ts', import.meta.url),
        { type: 'module' },
      )
      worker.port.start()
      worker.port.postMessage({ type: 'connect', token })
      worker.port.onmessage = (msg: MessageEvent) => {
        const m = msg.data as
          | { type: 'event'; data: string }
          | { type: 'connected' }
          | { type: 'error' }
          | undefined
        if (!m) return
        if (m.type === 'event' && typeof m.data === 'string') onEvent(m.data)
        else if (m.type === 'connected') onConnected()
        else if (m.type === 'error') onError()
      }
      return {
        close: () => {
          try {
            worker.port.postMessage('disconnect')
          } catch {
            /* port already dead */
          }
          try {
            worker.port.close()
          } catch {
            /* port already dead */
          }
        },
      }
    } catch {
      // Cai pro fallback EventSource direto abaixo.
    }
  }
  const es = new EventSource(
    token ? `${SSE_PATH}?token=${encodeURIComponent(token)}` : SSE_PATH,
    { withCredentials: true },
  )
  es.onmessage = (e) => onEvent(e.data)
  es.addEventListener('connected', () => onConnected())
  es.onopen = () => onConnected()
  es.onerror = () => onError()
  return { close: () => es.close() }
}

export function useNotificationsRealtime(
  filters: Record<string, unknown> = {},
  opts: UseRealtimeOptions = {},
): UseRealtimeReturn {
  const [newItemsCount, setNewItemsCount] = useState(0)
  const [connected, setConnected] = useState(false)
  const connRef = useRef<SseConnection | null>(null)
  const filtersKey = JSON.stringify(filters)
  // Ref estável pro onEvent: o caller pode passar função inline sem forçar
  // reconexão da SSE (o effect só depende de filtersKey).
  const onEventRef = useRef(opts.onEvent)
  onEventRef.current = opts.onEvent

  // Reset do counter sempre que filtros mudam (refetch implicito na pagina).
  useEffect(() => {
    setNewItemsCount(0)
  }, [filtersKey])

  useEffect(() => {
    let cancelled = false

    function open() {
      // NÃO condiciona à visibilidade: mantém o SSE aberto mesmo em background
      // para entrega em tempo real com a aba atrás.
      if (cancelled || typeof document === 'undefined') return
      if (connRef.current) return
      try {
        connRef.current = openSseConnection(
          (data) => {
            if (cancelled) return
            try {
              const payload = JSON.parse(data) as RealtimeEvent
              // onEvent recebe TODOS os eventos (o consumidor filtra como quiser).
              try { onEventRef.current?.(payload) } catch { /* consumidor não derruba a SSE */ }
              if (matchesFilters(payload, filters)) {
                setNewItemsCount((c) => c + 1)
              }
            } catch {
              /* ignore malformed payload */
            }
          },
          () => {
            if (!cancelled) setConnected(true)
          },
          () => {
            // EventSource auto-reconecta em erros transientes; apenas marcamos
            // o estado como desconectado para a UI refletir. No caminho do
            // SharedWorker o worker tambem cuida do retry interno (EventSource
            // dele tem mesmo auto-reconnect).
            if (!cancelled) setConnected(false)
          },
        )
      } catch {
        /* ignore — usuario offline ou SSE indisponivel */
      }
    }

    function close() {
      if (connRef.current) {
        connRef.current.close()
        connRef.current = null
      }
      setConnected(false)
    }

    function handleVis() {
      // Mantém viva em background (não fecha ao ocultar). Ao voltar o foco,
      // reabre apenas se a conexão tiver caído nesse meio-tempo.
      if (!document.hidden && !connRef.current) open()
    }

    open()
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', handleVis)
    }
    return () => {
      cancelled = true
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', handleVis)
      }
      close()
    }
    // filtersKey gateia a reconexao quando filtros mudam (re-abre subscription).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtersKey])

  return {
    newItemsCount,
    resetCount: () => setNewItemsCount(0),
    connected,
  }
}

function matchesFilters(ev: RealtimeEvent, filters: Record<string, unknown>): boolean {
  // Eventos de chat trafegam no mesmo feed SSE (migração WS→SSE) mas NÃO são
  // notificações — não podem inflar o badge/contador de novos itens.
  if (
    typeof ev.type === 'string' &&
    (ev.type.startsWith('message.') || ev.type.startsWith('reaction.') || ev.type === 'read')
  ) {
    return false
  }
  // Eventos de onboarding ZTNA (progresso/terminal) trafegam no mesmo feed SSE,
  // mas são consumidos pelo dashboard ZTNA — não inflam o contador de notificações.
  if (typeof ev.type === 'string' && ev.type.startsWith('ztna.job.')) {
    return false
  }
  const sources = filters.sources as string[] | undefined
  if (sources && sources.length > 0 && ev.source && !sources.includes(ev.source)) {
    return false
  }
  const sev = filters.severity as string[] | undefined
  if (sev && sev.length > 0 && ev.severity && !sev.includes(ev.severity)) {
    return false
  }
  return true
}
